import { Alert, Button } from '@root/components'
import { useBusinessContext } from '@root/context'
import {
  BlueprintBalanceEntry,
  BlueprintBalanceResult,
  fetchBlueprintBalance,
  IAccountingRule,
  IAccountingRuleBlueprintEntry
} from '@root/query'
import { formatEurosWithOpts } from '@root/utils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaBalanceScale } from 'react-icons/fa'
import styled from 'styled-components'

const DEBOUNCE_MS = 500

const snapshotEntries = (
  entries: IAccountingRuleBlueprintEntry[] | undefined
): BlueprintBalanceEntry[] =>
  (entries ?? []).map(entry => ({
    account_id: entry.account_id ?? null,
    amount: String(entry.amount ?? '').trim()
  }))

const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timeout)
  }, [value, delay])

  return debounced
}

const BalanceCheckBar: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { control } = useFormContext<IAccountingRule>()
  const blueprint = useWatch({ control, name: 'blueprint' })

  const snapshot = useMemo(
    () => ({
      debetEntries: snapshotEntries(blueprint?.debet_entries),
      creditEntries: snapshotEntries(blueprint?.credit_entries)
    }),
    [blueprint]
  )
  const debounced = useDebounced(snapshot, DEBOUNCE_MS)
  const ready =
    debounced.debetEntries.length > 0 &&
    debounced.creditEntries.length > 0 &&
    debounced.debetEntries.every(entry => entry.amount.length > 0) &&
    debounced.creditEntries.every(entry => entry.amount.length > 0)

  const balanceQuery = useQuery<BlueprintBalanceResult>({
    queryKey: ['automation-blueprint-balance', slug, debounced],
    queryFn: () =>
      fetchBlueprintBalance({
        slug,
        debetEntries: debounced.debetEntries,
        creditEntries: debounced.creditEntries
      }),
    enabled: ready && Boolean(slug),
    retry: false,
    placeholderData: keepPreviousData
  })

  if (!ready) return null

  if (balanceQuery.isError) {
    return (
      <Alert
        type="error"
        marginBottom={0}
        description={
          <>
            {t('settings.automation.accountingRules.form.balanceFailed')}{' '}
            <Button type="button" size="sm" onClick={() => balanceQuery.refetch()}>
              {t('settings.automation.accountingRules.form.previewRetry')}
            </Button>
          </>
        }
      />
    )
  }

  if (!balanceQuery.data) {
    return (
      <Alert
        type="info"
        marginBottom={0}
        description={t('settings.automation.accountingRules.form.balanceChecking')}
      />
    )
  }

  const result = balanceQuery.data
  const debit = formatEurosWithOpts(result.debet_total)
  const credit = formatEurosWithOpts(result.credit_total)
  const sample = formatEurosWithOpts(result.sample_subunits)
  const detail = result.balanced
    ? t('settings.automation.accountingRules.form.balanceOkDetail', { debit, credit })
    : t('settings.automation.accountingRules.form.balanceBadDetail', {
        difference: formatEurosWithOpts(result.difference)
      })

  return (
    <Alert
      type={result.balanced ? 'success' : 'warning'}
      icon={result.balanced ? FaBalanceScale : undefined}
      marginBottom={0}
      title={
        <BalanceCopy>
          <BalanceText>
            <BalanceHeading>
              {t(
                result.balanced
                  ? 'settings.automation.accountingRules.form.balanceOk'
                  : 'settings.automation.accountingRules.form.balanceBad'
              )}
            </BalanceHeading>
            <span>{detail}</span>
          </BalanceText>
          <BalanceSample>
            {t('settings.automation.accountingRules.form.balanceSample', { amount: sample })}
          </BalanceSample>
        </BalanceCopy>
      }
    />
  )
}

export default BalanceCheckBar

const BalanceCopy = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  font-weight: 400;
`

const BalanceText = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const BalanceHeading = styled.span`
  font-weight: 700;
`

const BalanceSample = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`
